import { readArchive } from '../archive';
import type { ArchiveReadReport, ParsedArchive } from '../archive';
import { putImportedBlob } from '../data/blobStore';
import { TABLE, write } from '../data/db';
import type { TableName } from '../data/db';
import {
  REPLACED_TABLES,
  applyProfileWipe,
  assertProfileEmpty,
  assertWrittenCounts,
  insertSetting,
  insertTemplate,
  planProfileWipe,
} from '../data/repos/profile';
import type { ProfileWipePlan } from '../data/repos/profile';
import { insertAnnotation } from '../data/repos/annotations';
import { insertCoachCard } from '../data/repos/coachCards';
import { insertOurPlayer } from '../data/repos/ourPlayers';
import { insertResolution } from '../data/repos/flagResolutions';
import { insertHypothesis, writeCountState } from '../data/repos/hypotheses';
import { insertMatch } from '../data/repos/matches';
import { insertNote } from '../data/repos/matchNotes';
import { insertObservation } from '../data/repos/observations';
import { upsertSetPiece } from '../data/repos/setPieces';
import { assertInv6, writeSelection } from '../data/repos/talkPoints';
import type { TalkPointSelection } from '../data/repos/talkPoints';
import { insertPlayer, insertTeam } from '../data/repos/teams';
import { reconcile } from '../domain/counts';
import { MATCH_STATE } from '../domain/machine';
import { ARTIFACT_PHASE } from '../types';
import type { ArtifactPhase, HypothesisRow, Status, StatusOverride, TalkPointRow } from '../types';
import { log } from './logger';
import { validateImport } from './importValidate';
import type { ArchiveInput, ImportValidation } from './importValidate';

/**
 * **BAK-2** — *"Import restores a backup zip into an empty profile, replace-only in v1."* — and,
 * because **SYN-5** says restore *"import[s] each through the BAK-2 import path — one code path,
 * already tested"*, this is also the restore.
 *
 * The caller hands over archive bytes and does not say where they came from. A file the user picked
 * and an object the drainer pulled from R2 differ in nothing this module can see, which is what
 * makes "one code path" true rather than aspirational.
 *
 * ── Two steps, and the split is BAK-4's ──────────────────────────────────────
 *
 * ```
 * const plan = await planImport(archives);   // parses, validates, counts. DESTROYS NOTHING.
 * // …show plan.willRemove and plan.willImport, get a confirm…
 * const outcome = await applyImport(plan);   // one transaction: wipe, write, reconcile.
 * ```
 *
 * There is deliberately **no one-shot `importArchive(bytes)`**. BAK-4 requires a confirm *"stating
 * the counts of what will be removed"*, and a convenience function that skipped the plan would be
 * the path every caller took. `applyImport` accepts only a `ImportPlan`, which only `planImport`
 * produces, so the counts always exist before the destruction does. `plan.willRemove` comes from the
 * same `ProfileWipePlan` object the wipe then executes — and `applyProfileWipe` re-plans inside the
 * transaction and aborts if the database drifted, so the number the user saw and the rows that go
 * are the same fact, not two queries (BAK-4, mirroring `repos/cascade.ts`).
 *
 * ── Replace-only, made structural rather than intended ───────────────────────
 *
 * Import-merge is a named non-goal (requirements §5), so "we do not merge" needs a mechanism, not a
 * comment. Four, each independently sufficient to stop a merge:
 *
 * 1. **No merge entry point exists.** This module exports two functions and neither takes a "keep
 *    existing rows" option. There is nothing to call.
 * 2. **The wipe is inside the same transaction as the writes.** `applyImport` opens one `write()`
 *    whose first act is `applyProfileWipe`. A caller cannot write rows without it, and cannot wipe
 *    without writing — either half failing rolls back both.
 * 3. **`assertProfileEmpty()` is an in-transaction postcondition.** After the wipe and before the
 *    first row, every replaced table is counted; a non-zero count aborts. Replace-only stops being a
 *    property of the *sequence* and becomes one the transaction enforces.
 * 4. **Every row is written with an `add`-shaped repo insert.** Writing into a profile that still
 *    held rows would abort on the first colliding id. Merge is not merely unimplemented; it fails.
 *
 * ── Everything that can refuse, refuses in step 1 ────────────────────────────
 *
 * `planImport` opens no transaction and removes nothing. `readArchive` throws there for an
 * unreadable container or a `formatVersion` this bundle cannot read (SYN-9), and `validateImport`
 * quarantines there. By the time anything is destroyed, the exact row set that will replace it is
 * already in hand — which is the only ordering a replace-only import can safely have.
 */

const MODULE = 'backup';

/**
 * A count set with its total.
 *
 * **The two on `ImportPlan` are keyed by different vocabularies and are not interchangeable.**
 * `willRemove` is keyed by **table name** — it comes from the wipe, which covers `outbox`,
 * `uiSession` and `activeMatch`, none of which is an archive entity. `willImport` is keyed by
 * **archive entity** — `ARCHIVE_ENTITIES`, which has no member for any of those three. A confirm
 * screen that indexed one with the other's key would silently render a blank.
 */
export interface ImportCounts {
  readonly byEntity: Readonly<Partial<Record<string, number>>>;
  readonly total: number;
}

export interface ImportPlan {
  /** One entry per archive, in application order (§11.9). */
  readonly sources: readonly ImportSource[];
  readonly validation: ImportValidation;
  /** BAK-4's "what will be lost". The same object `applyImport` executes. */
  readonly wipe: ProfileWipePlan;
  readonly willRemove: ImportCounts;
  readonly willImport: ImportCounts;
}

export interface ImportSource {
  readonly label: string;
  readonly formatVersion: number;
  readonly scope: string;
  readonly generatedFor: string;
  readonly report: ArchiveReadReport;
}

export interface ImportOutcome {
  readonly plan: ImportPlan;
  /**
   * Rows written per entity.
   *
   * The numbers are the plan's, but they are **verified against the committed rows** by
   * `assertWrittenCounts` inside the transaction — so a writer that dropped or added one aborts
   * rather than leaving this field describing something that did not happen.
   */
  readonly written: ImportCounts;
  /**
   * INV-2 repairs: hypotheses whose archived `cachedCount`/`status` disagreed with a replay of the
   * archive's own Observations. Empty for an archive this app produced.
   */
  readonly reconciledDrift: readonly string[];
  /** `committedAt` from the native `complete` event — the write is confirmed, not assumed (SES-2). */
  readonly committedAt: number;
}

/**
 * Step 1: read, validate, and count. **Opens no transaction and removes nothing.**
 *
 * @param archives in the order §11.9 requires for a restore — match archives ascending by manifest
 *   `ts`, then the profile archive last. A BAK-2 import is a list of one.
 * @throws {ArchiveError} when an archive is not a readable Touchline backup, or its `formatVersion`
 *   is newer than this bundle (SYN-9). Nothing on the device has been touched.
 * @throws {StorageError} on a read failure while counting what would be replaced.
 */
export async function planImport(archives: readonly ArchiveInput[]): Promise<ImportPlan> {
  const parsed: ParsedArchive[] = [];
  const sources: ImportSource[] = [];

  archives.forEach((input, index) => {
    const label = input.label ?? `archive ${index + 1}`;
    let archive: ParsedArchive;
    try {
      archive = readArchive(input.bytes);
    } catch (cause) {
      // NFR-8, and the most likely failure this module has: the user picked the wrong file, or a
      // newer client's backup. The caller surfaces the message; the ring is what the debug screen
      // shows afterwards, so a refusal that left no trace would be unanswerable at a venue.
      log.exception(MODULE, 'a file could not be read as a Touchline backup', cause, {
        label,
        bytes: input.bytes.length,
      });
      throw cause;
    }
    parsed.push(archive);
    sources.push({
      label,
      formatVersion: archive.formatVersion,
      scope: archive.scope.kind,
      generatedFor: archive.generatedFor,
      report: archive.report,
    });
  });

  const validation = validateImport(parsed);
  const wipe = await planProfileWipe();

  return {
    sources,
    validation,
    wipe,
    willRemove: { byEntity: wipe.counts, total: wipe.total },
    willImport: {
      byEntity: validation.counts,
      total: Object.values(validation.counts).reduce((sum, n) => sum + n, 0),
    },
  };
}

/**
 * Step 2: empty the profile and write the plan, in **one** transaction.
 *
 * The reconcile pass and the INV-6 postcondition run inside it too, and that is not tidiness. INV-2
 * says `cachedCount` equals the count of `increment` observations *"at all times after any
 * transaction commits"* — reconciling in a second transaction would leave a committed profile that
 * violates it in between. INV-6's assertion has to abort rather than report for the same reason.
 *
 * @throws {StorageError} if the profile drifted since the plan was taken
 *   (`context.staleProfileWipe`), if the profile is not empty after the wipe, or on any write
 *   failure. Every one aborts the transaction: the profile is either fully replaced or untouched.
 */
export async function applyImport(plan: ImportPlan): Promise<ImportOutcome> {
  const { rows } = plan.validation;
  const reconciledDrift: string[] = [];

  const result = await write(
    REPLACED_TABLES,
    async () => {
      await applyProfileWipe(plan.wipe);
      // Mechanism 3. Replace-only is now enforced by the transaction, not by this function's order.
      await assertProfileEmpty();

      for (const row of rows.teams) await insertTeam(row);
      for (const row of rows.players) await insertPlayer(row);
      for (const row of rows.matches) await insertMatch(row);
      // `insertHypothesis` runs INV-5's in-transaction board-cap postcondition per row, so an
      // archive carrying a ninth positioned tile aborts the import rather than restoring a board
      // no screen can render (design §4: "on every path, including an import").
      for (const row of rows.hypotheses) await insertHypothesis(row);
      for (const row of rows.observations) await insertObservation(row);
      for (const row of rows.flagResolutions) await insertResolution(row);
      for (const row of rows.setPieces) await upsertSetPiece(row);

      // `writeSelection` is the only writer of `TalkPoint.rank` and it rewrites the set as `1..n`
      // (§10.2). Importing row-by-row would need a second rank writer, and "next free rank" is the
      // trap that module exists to foreclose — so the import supplies the ordered selection instead.
      // Any renumbering it performs was detected and reported at plan time.
      for (const [matchId, phase, ordered] of groupTalkPoints(rows.talkPoints)) {
        await writeSelection(matchId, phase, ordered);
      }

      for (const row of rows.matchNotes) await insertNote(row);
      // B-1: the two match-scoped B entities. `insertCoachCard` rides the unique
      // `&[matchId+context]` index, so an archive carrying two cards for one break aborts here.
      for (const row of rows.annotations) await insertAnnotation(row);
      for (const row of rows.coachCards) await insertCoachCard(row);

      for (const blob of rows.audioBlobs) {
        await putImportedBlob({
          id: blob.id,
          // A metadata row whose bytes the archive did not carry is restored **without** bytes
          // rather than with an empty blob: an empty one would become a `blobs/<id>` entry on the
          // next export that the source archive did not have, and BAK-2's round trip would stop
          // being byte-stable. D-19's "something happened at minute 34" survives on the Observation.
          blob: blob.data === null ? null : new Blob([blob.data.slice().buffer as ArrayBuffer]),
          mime: blob.mime,
          bytes: blob.bytes,
          complete: blob.complete,
          createdAt: blob.createdAt,
        });
      }

      for (const row of rows.templates) await insertTemplate(row);
      for (const row of rows.settings) await insertSetting(row);
      // B-1: the profile-scoped roster, replaced like templates and settings.
      for (const row of rows.ourPlayers) await insertOurPlayer(row);

      // ── The reconcile pass (§16 U-6's stated dependency on U-2) ─────────────
      //
      // INV-2 holds "after any transaction commits", and an import writes rows wholesale — the
      // archive's `cachedCount` is whatever the source device last wrote, and a damaged or
      // hand-edited archive can disagree with its own Observation log. `domain/counts.reconcile`
      // is `replay`, the same fold the live handlers agree with by construction, so this is a
      // recount rather than a second opinion.
      //
      // INV-9 as amended in revision 2 is the reason `overrides` is threaded in rather than
      // recomputed: replay reproduces counts and **count-derived** status, holding the user's
      // `disproved`/`invalidated` constant. A pass that derived status from counts alone would
      // silently un-disprove every hypothesis the user rejected — and PRE-5 would then re-offer
      // exactly those as board candidates after a phone-loss restore (§3.3).
      const overrides = new Map<string, StatusOverride>(
        rows.hypotheses.map((row) => [row.id, row.statusOverride]),
      );
      const reconciled = reconcile(rows.observations, overrides);
      const archivedById = new Map<string, HypothesisRow>(
        rows.hypotheses.map((row) => [row.id, row]),
      );
      for (const [id, state] of reconciled) {
        const archivedRow = archivedById.get(id);
        if (archivedRow && driftsFrom(archivedRow, state.cachedCount, state.status)) {
          reconciledDrift.push(
            `${id}: archived count ${archivedRow.cachedCount}/${archivedRow.status}, replay ${state.cachedCount}/${state.status}`,
          );
        }
        await writeCountState(id, state);
      }

      // INV-6's postcondition, run through the same function `CompleteDistillation` uses. Plan-time
      // validation already quarantined every violating match, so this firing means that check has a
      // bug — and an abort is the only response that does not commit the consequence of one.
      for (const match of rows.matches) {
        if (match.state === MATCH_STATE.archived && !match.abandoned) await assertInv6(match);
      }

      // And the row counts the caller will be shown are counted, not assumed.
      await assertWrittenCounts(writtenCounts(rows));
    },
    { label: 'BAK-2 import' },
  ).catch((cause: unknown) => {
    // NFR-8: a **failed** import is the case that most needs a record. Without this the venue
    // scenario — a destructive operation that refused — leaves nothing in the OBS-1 ring at all.
    // Logged and re-thrown unchanged, so the caller still sees its own typed category.
    log.exception(MODULE, 'the import failed and nothing was changed', cause, {
      sources: plan.sources.map((source) => source.label),
      wouldHaveRemoved: plan.willRemove.total,
      wouldHaveImported: plan.willImport.total,
    });
    throw cause;
  });

  const outcome: ImportOutcome = {
    plan,
    written: plan.willImport,
    reconciledDrift,
    committedAt: result.receipt.committedAt,
  };
  logImportOutcome(outcome);
  return outcome;
}

/**
 * The plan's row counts, keyed by **table** so `assertWrittenCounts` can count them.
 *
 * `willImport` is keyed by archive entity, and the two vocabularies are only coincidentally equal
 * for the twelve entities that have a table — the mapping is spelled out rather than assumed so a
 * future entity with a different table name does not silently drop out of the check.
 */
function writtenCounts(rows: ImportPlan['validation']['rows']): Partial<Record<TableName, number>> {
  return {
    [TABLE.teams]: rows.teams.length,
    [TABLE.players]: rows.players.length,
    [TABLE.matches]: rows.matches.length,
    [TABLE.hypotheses]: rows.hypotheses.length,
    [TABLE.observations]: rows.observations.length,
    [TABLE.flagResolutions]: rows.flagResolutions.length,
    [TABLE.setPieces]: rows.setPieces.length,
    [TABLE.talkPoints]: rows.talkPoints.length,
    [TABLE.matchNotes]: rows.matchNotes.length,
    [TABLE.annotations]: rows.annotations.length,
    [TABLE.coachCards]: rows.coachCards.length,
    [TABLE.ourPlayers]: rows.ourPlayers.length,
    [TABLE.audioBlobs]: rows.audioBlobs.length,
    [TABLE.templates]: rows.templates.length,
    [TABLE.settings]: rows.settings.length,
  };
}

/** Rank order within a `(matchId, phase)` set; the plan already sorted them. */
function groupTalkPoints(
  talkPoints: readonly TalkPointRow[],
): [string, ArtifactPhase, TalkPointSelection[]][] {
  const groups = new Map<string, [string, ArtifactPhase, TalkPointSelection[]]>();
  for (const row of talkPoints) {
    const key = `${row.matchId}/${row.phase}`;
    const group = groups.get(key) ?? [row.matchId, row.phase, []];
    group[2].push(toSelection(row));
    groups.set(key, group);
  }
  return [...groups.values()];
}

/**
 * Drops `rank` while keeping the INV-13 discrimination.
 *
 * Written out per variant rather than as a rest-spread: a spread over a discriminated union widens
 * it to the intersection of its members, which is exactly the collapse `TalkPointSelection`'s own
 * declaration warns about — and it would make a `fullTime` row with no instruction text typecheck.
 */
function toSelection(row: TalkPointRow): TalkPointSelection {
  return row.phase === ARTIFACT_PHASE.fullTime
    ? {
        id: row.id,
        matchId: row.matchId,
        phase: row.phase,
        instructionText: row.instructionText,
        sourceHypothesisId: row.sourceHypothesisId,
        attachedSetPieceId: row.attachedSetPieceId,
      }
    : {
        id: row.id,
        matchId: row.matchId,
        phase: row.phase,
        instructionText: row.instructionText,
        sourceHypothesisId: row.sourceHypothesisId,
        attachedSetPieceId: row.attachedSetPieceId,
      };
}

function driftsFrom(row: HypothesisRow, count: number, status: Status): boolean {
  return row.cachedCount !== count || row.status !== status;
}

/**
 * Surfaces everything the import decided. NFR-8: reported or recovered, never swallowed.
 *
 * Logged **after** the commit rather than inside the transaction: an IndexedDB transaction dies on
 * any non-IDB await, and the logger's flush is an IDB write on its own connection. Nothing in here
 * is on a capture path, so `fire-and-forget-observability`'s usual caution does not apply — but the
 * ordering rule does.
 */
function logImportOutcome(outcome: ImportOutcome): void {
  const { plan } = outcome;
  const v = plan.validation;

  log.info(MODULE, 'imported a backup, replacing the profile', {
    sources: plan.sources.map((source) => ({
      label: source.label,
      scope: source.scope,
      formatVersion: source.formatVersion,
      generatedFor: source.generatedFor,
    })),
    removed: plan.willRemove,
    written: outcome.written,
    committedAt: outcome.committedAt,
  });

  for (const source of plan.sources) {
    const r = source.report;
    if (r.deniedSettingKeys.length > 0) {
      // §11.8's exclusion, from the import side. Reaching this line means an archive carried a key
      // no archive should contain — the credential that authenticates the backup service among
      // them — so it is worth an error even though the import continued without it.
      log.error(MODULE, 'an archive carried settings that must never be in one; they were dropped', {
        label: source.label,
        keys: r.deniedSettingKeys,
      });
    }
    if (r.unknownEntities.length > 0 || r.unexpectedEntries.length > 0) {
      log.warn(MODULE, 'an archive carried content this version does not understand', {
        label: source.label,
        entities: r.unknownEntities,
        entries: r.unexpectedEntries,
      });
    }
    if (r.countMismatches.length > 0) {
      log.warn(MODULE, 'an archive’s manifest disagrees with its own data', {
        label: source.label,
        mismatches: r.countMismatches,
      });
    }
    if (r.metadataWithoutBlobs.length > 0) {
      log.warn(MODULE, 'an archive listed audio it did not carry', {
        label: source.label,
        ids: r.metadataWithoutBlobs,
      });
    }
  }

  if (v.quarantinedMatches.length > 0) {
    log.error(MODULE, 'matches were quarantined and not imported', {
      matches: v.quarantinedMatches,
    });
  }
  if (v.quarantinedRows.length > 0) {
    log.error(MODULE, 'rows failed validation and were not imported', {
      rows: v.quarantinedRows.slice(0, 50),
      total: v.quarantinedRows.length,
    });
  }
  if (v.droppedForMissingOwner.length > 0) {
    log.warn(MODULE, 'rows were dropped because the row they belong to was not imported', {
      dropped: v.droppedForMissingOwner,
    });
  }
  if (v.blobBytesWithoutMetadata.length > 0) {
    log.error(MODULE, 'audio files in the archive had no metadata row and could not be restored', {
      ids: v.blobBytesWithoutMetadata,
    });
  }
  if (v.blobsRestoredEmpty.length > 0) {
    log.warn(MODULE, 'audio rows were restored with no bytes behind them', {
      ids: v.blobsRestoredEmpty,
    });
  }
  if (v.blobSizeMismatches.length > 0) {
    log.warn(MODULE, 'audio byte counts disagree with the files that carried them', {
      mismatches: v.blobSizeMismatches,
    });
  }
  if (v.normalizedTalkPointRanks.length > 0) {
    log.warn(MODULE, 'talk-point ranks were renumbered to close a gap', {
      groups: v.normalizedTalkPointRanks,
    });
  }
  if (v.danglingReferences.length > 0) {
    log.warn(MODULE, 'imported rows reference rows that do not exist (OBS-2 orphan report)', {
      references: v.danglingReferences,
    });
  }
  if (outcome.reconciledDrift.length > 0) {
    log.error(MODULE, 'INV-2: archived counts disagreed with the archive’s own observation log', {
      drift: outcome.reconciledDrift,
    });
  }
}

export type { ArchiveInput, ImportValidation };
